/** Session storage key of when the page last reloaded because an asset of the app was missing */
const RELOAD_AT_KEY = "stale-assets-reloaded-at";

/**
 * Shortest time between two such reloads. An asset still missing right after a reload is missing
 * for another reason, so the error is let through instead of reloading in a loop.
 */
export const MIN_STALE_ASSETS_RELOAD_INTERVAL_MS = 10_000;
/**
 * Handles an asset of the app failing to load, e.g. the code of the calendar or of the point cloud
 * preview, which are only loaded once needed. A new deployment replaces them with files of other
 * names, so a page opened before it asks for files that no longer exist. Reloading loads the new
 * deployment instead, rather than the page breaking.
 *
 * @returns whether the page is reloaded; otherwise the error goes on as usual
 */
export function reloadOnStaleAssets(event, {
  storage,
  reload,
  now
}) {
  const lastReloadAt = Number(storage.getItem(RELOAD_AT_KEY) ?? 0);
  if (now() - lastReloadAt < MIN_STALE_ASSETS_RELOAD_INTERVAL_MS) {
    return false;
  }
  storage.setItem(RELOAD_AT_KEY, String(now()));
  // Keeps the error from being thrown while the page reloads
  event.preventDefault();
  reload();
  return true;
}

/**
 * Reloads the page when an asset loaded on demand is missing, e.g. after a new deployment.
 * Vite reports these failures with the `vite:preloadError` event.
 */
export function listenForStaleAssets(dependencies = {
  storage: window.sessionStorage,
  reload: () => window.location.reload(),
  now: Date.now
}) {
  function onPreloadError(event) {
    reloadOnStaleAssets(event, dependencies);
  }
  window.addEventListener("vite:preloadError", onPreloadError);
  return () => window.removeEventListener("vite:preloadError", onPreloadError);
}